import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { PortalModule, type MyAccessDto } from '@ciimar/shared';
import { api } from '../api/client';
import { useAuth } from './AuthContext';

interface AccessState {
  access: MyAccessDto | null;
  loading: boolean;
  /** True when the user may open the module (admin → always). */
  has: (module: PortalModule) => boolean;
  /** True when the user may edit in the module (admin → always). */
  canEdit: (module: PortalModule) => boolean;
  reload: () => void;
}

const AccessContext = createContext<AccessState | undefined>(undefined);

/**
 * Resolves the signed-in user's module access once (`/permissions/modules/me`)
 * and shares it with the shell (navigation) and the route guards. Everyone has
 * the implicit "Meu Perfil"; `access.modules` are the extra modules they may
 * open. Admins get everything.
 */
export function AccessProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [access, setAccess] = useState<MyAccessDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!user) {
      setAccess(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    api
      .myModuleAccess()
      .then((a) => active && setAccess(a))
      .catch(
        () =>
          active &&
          setAccess({
            personId: null,
            personName: null,
            isAdmin: false,
            isAdminIt: false,
            modules: [],
          }),
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [user, tick]);

  const value = useMemo<AccessState>(() => {
    // Definições is the core configuration — reserved to IT admins, so it is
    // gated on isAdminIt rather than the blanket admin/grant access.
    const has = (module: PortalModule) =>
      !!access &&
      (module === PortalModule.Definicoes
        ? access.isAdminIt
        : access.isAdmin || access.modules.some((m) => m.module === module));
    const canEdit = (module: PortalModule) =>
      !!access &&
      (module === PortalModule.Definicoes
        ? access.isAdminIt
        : access.isAdmin || access.modules.some((m) => m.module === module && m.canEdit));
    return { access, loading, has, canEdit, reload: () => setTick((t) => t + 1) };
  }, [access, loading]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess(): AccessState {
  const ctx = useContext(AccessContext);
  if (!ctx) throw new Error('useAccess must be used within AccessProvider');
  return ctx;
}
